/**
 * Modo de pedido de categoria única ("só restaurantes", "only viewpoints").
 *
 * Este módulo é ADITIVO: só devolve resultado quando o utilizador escreve
 * explicitamente "só/apenas/somente/only/just/nur/solo/seulement". Sem essas
 * palavras devolve `null` e o comportamento Premium atual mantém-se igual.
 */

export type ExclusiveCategory =
  | "restaurant"
  | "viewpoint"
  | "museum"
  | "beach"
  | "nature_park"
  | "amusement_park"
  | "water_park"
  | "traditional_sweets"
  | "breakfast";

export type ExclusiveCategoryRequest = {
  category: ExclusiveCategory;
  /** Query em inglês para Places. */
  searchQuery: string;
  /** Só restaurantes: distribuir por refeições em vez de cinco seguidas. */
  mealSlots: boolean;
};

const W = "(?<![\\p{L}])";
const Wend = "(?![\\p{L}])";

const EXCLUSIVE_MARKER = new RegExp(
  `${W}(so|só|apenas|somente|unicamente|only|just|nada\\s+mais|nur|solo|solamente|seulement|uniquement)${Wend}`,
  "iu",
);

const NEEDS_ZONE_MARKER = new RegExp(
  `${W}(melhor|melhores|best|mejor|migliore|meilleur)${Wend}`,
  "iu",
);

const PATTERNS: Array<{ id: ExclusiveCategory; re: RegExp; query: string }> = [
  {
    id: "water_park",
    re: new RegExp(`${W}(parques?\\s+aqu[aá]ticos?|water\\s?parks?|aquaparks?|parques?\\s+acu[aá]ticos?)${Wend}`, "iu"),
    query: "water park",
  },
  {
    id: "amusement_park",
    re: new RegExp(`${W}(parques?\\s+de\\s+divers[oõ]es?|parques?\\s+tem[aá]ticos?|amusement\\s+parks?|theme\\s+parks?)${Wend}`, "iu"),
    query: "amusement park",
  },
  {
    id: "nature_park",
    re: new RegExp(`${W}(parques?\\s+naturais?|parques?\\s+nacionais?|reservas?\\s+naturais?|nature\\s+parks?|national\\s+parks?)${Wend}`, "iu"),
    query: "nature park",
  },
  {
    id: "beach",
    re: new RegExp(`${W}(praias?|beaches?|playas?|plages?)${Wend}`, "iu"),
    query: "beach",
  },
  {
    id: "museum",
    re: new RegExp(`${W}(museus?|museums?|mus[eé]es?|museos?)${Wend}`, "iu"),
    query: "museum",
  },
  {
    id: "viewpoint",
    re: new RegExp(`${W}(miradour\\w+|viewpoints?|overlooks?|miradores?)${Wend}`, "iu"),
    query: "scenic viewpoint",
  },
  {
    id: "restaurant",
    re: new RegExp(`${W}(restaurantes?|restaurants?|tascas?|onde\\s+comer|places?\\s+to\\s+eat)${Wend}`, "iu"),
    query: "highly rated restaurant",
  },
  {
    id: "breakfast",
    re: new RegExp(
      `${W}(pequenos?[\\s-]*almo[cç]os?|brunch(?:es)?|breakfasts?|desayunos?|petits?[\\s-]*d[ée]jeuners?|colazione|fr[üu]hst[üu]ck)${Wend}`,
      "iu",
    ),
    query: "breakfast cafe bakery brunch",
  },
  {
    id: "traditional_sweets",
    re: new RegExp(
      `${W}(doces?\\s+(?:regionais?|t[ií]picos?|tradicionais?|conventuais?)|do[cç]aria|pastelarias?|padarias?\\s+(?:t[ií]picas?|tradicionais?)|confeitarias?|sweets?\\s+shop|pastry\\s+shop|patisserie|dolci\\s+t[ií]pici|pasteles?\\s+t[ií]picos)${Wend}`,
      "iu",
    ),
    query: "traditional sweets shop pastry bakery",
  },
];

/**
 * Deteta um pedido explícito de categoria única.
 * Nota: "parques naturais" nunca é confundido com "parques de diversões"
 * porque os padrões específicos são avaliados primeiro.
 */
/**
 * "rota/roteiro/percurso de doces típicos" é, por si só, um pedido exclusivo
 * de doçaria: a rota é definida pela categoria, não precisa de "só".
 */
const SWEETS_ROUTE_MARKER = new RegExp(
  `${W}(rota|rotas|roteiro\\w*|percurso\\w*|itiner[aá]rio\\w*|tour|route|circuito|ruta)${Wend}\\s+(?:de\\s+|of\\s+|dos\\s+|das\\s+|d[oa]\\s+)?(?:doces?\\s+(?:regionais?|t[ií]picos?|tradicionais?|conventuais?)|do[cç]aria|pastelarias?|confeitarias?|traditional\\s+sweets|pastry\\s+shops?)`,
  "iu",
);

export function detectExclusiveCategory(text: string): ExclusiveCategoryRequest | null {
  if (!text?.trim()) return null;
  const sweetsRoute = SWEETS_ROUTE_MARKER.test(text);
  if (!EXCLUSIVE_MARKER.test(text) && !sweetsRoute) return null;
  const match = sweetsRoute
    ? PATTERNS.find(({ id }) => id === "traditional_sweets")!
    : PATTERNS.find(({ re }) => re.test(text));
  if (!match) return null;
  return {
    category: match.id,
    searchQuery: match.query,
    mealSlots: match.id === "restaurant",
  };
}


/**
 * "melhor parque aquático" sem zona: pergunta gratuita antes de gerar.
 * Devolve `null` quando já existe destino ou quando não há superlativo.
 */
export function zoneQuestionForBestVenue(
  text: string,
  hasDestination: boolean,
): { question: string; category: ExclusiveCategory } | null {
  if (hasDestination || !text?.trim()) return null;
  if (!NEEDS_ZONE_MARKER.test(text)) return null;
  const match = PATTERNS.find(({ re }) => re.test(text));
  if (!match) return null;
  return { question: "Em que zona queres ir?", category: match.id };
}

/** Distribuição realista numa rota só de restaurantes. */
export function restaurantMealSlots(count: number): Array<{ slot: string; time: string }> {
  const base = [
    { slot: "breakfast", time: "09:00" },
    { slot: "lunch", time: "13:00" },
    { slot: "dinner", time: "20:00" },
  ];
  if (count <= 3) return base.slice(0, Math.max(1, count));
  // Extra venues são alternativas do mesmo slot, não refeições adicionais.
  return base;
}

/* ------------------------------------------------------------------ *
 * Filtro RÍGIDO de categoria (ramo Premium exclusivo)
 *
 * Quando o pedido é exclusivo ("só restaurantes") ou o formulário tem uma
 * única categoria escolhida, nenhum POI de outra categoria pode chegar ao
 * ecrã. Este validador é puro e corre no servidor antes da renderização.
 * ------------------------------------------------------------------ */

/** Ids de foco usados pelo motor Premium (`categoryFocus`). */
export type FocusCategory =
  | "cafe" | "restaurant" | "hotel" | "viewpoint" | "museum" | "monument"
  | "church" | "park" | "nature" | "water" | "shopping" | "nightlife" | "market"
  | "sweets" | "breakfast";

/** Mapeia o pedido exclusivo detetado no texto para o foco do motor. */
export const EXCLUSIVE_TO_FOCUS: Record<ExclusiveCategory, FocusCategory> = {
  restaurant: "restaurant",
  viewpoint: "viewpoint",
  museum: "museum",
  beach: "water",
  nature_park: "nature",
  amusement_park: "park",
  water_park: "park",
  traditional_sweets: "sweets",
  breakfast: "breakfast",
};

/** Interesse único selecionado no formulário → foco vinculativo. */
export const SINGLE_INTEREST_FOCUS: Record<string, FocusCategory> = {
  food: "restaurant",
  viewpoint: "viewpoint",
  nature: "nature",
  shopping: "shopping",
  nightlife: "nightlife",
};

const foldText = (value: string) =>
  (value ?? "").normalize("NFD").replace(/[\u0300-\u036f]/gu, "").toLowerCase();

const ALLOW: Record<FocusCategory, RegExp> = {
  restaurant:
    /restaurant|restaurante|tasca|tasquinha|marisqueira|churrasqueira|cervejaria|bistro|brasserie|trattoria|osteria|pizzer|steak|sushi|ramen|taberna|taverna|petisc|tapas|snack|brunch|food\s?hall|food\s?court|gastron|comida|eatery|dining|cafe|caf[eé]|coffee|pastelaria|pasteleria|padaria|bakery|patisserie|confeitaria|gelat|sorvet|ice\s?cream|wine\s?bar|bar\b|pub\b|cantina|casa\s+de\s+pasto/u,
  cafe: /cafe|caf[eé]|coffee|pastelaria|padaria|bakery|patisserie|confeitaria|tea\s?room|casa\s+de\s+ch[aá]|brunch|gelat|sorvet/u,
  hotel: /hotel|hostel|pousada|guesthouse|guest\s?house|alojament|lodge|resort|inn\b|bed\s+and\s+breakfast|b&b/u,
  viewpoint: /miradour|viewpoint|overlook|panoram|vista|belvedere|mirador|rooftop|observation\s?deck|scenic/u,
  museum: /museu|museum|mus[eé]e|museo|galeria|gallery|pinacoteca|centro\s+interpretativo|interpretation\s+cent/u,
  monument: /monument|memorial|est[aá]tua|statue|castelo|castle|forte|fortaleza|palac|pal[aá]cio|torre|tower|ru[ií]nas|ruins|landmark|arco|aqueduto/u,
  church: /igreja|church|catedral|cathedral|capela|chapel|s[eé]\s|mosteiro|monastery|convento|convent|santu[aá]rio|sanctuary|basilica|bas[ií]lica|templo|temple|synagog|sinagoga|mosque|mesquita/u,
  park: /parque|park|jardim|garden|amusement|tem[aá]tico|theme\s?park|aqu[aá]tico|water\s?park|aquapark|zoo|playground|recreation/u,
  nature: /natur|parque\s+natural|parque\s+nacional|national\s+park|nature\s+reserve|reserva|trilho|trail|hiking|serra|montanha|mountain|floresta|forest|mata|lagoa|cascata|waterfall|geopark|dunas/u,
  water: /praia|beach|playa|plage|rio\b|river|lago|lake|lagoa|lagoon|mar\b|sea\b|costa|coast|marina|waterfront|ribeira|cais|pier|promenade|piscina\s+natural/u,
  shopping: /shopping|mall|loja|shop\b|store|boutique|mercado\s+de\s+artesanato|outlet|centro\s+comercial|bazar/u,
  nightlife: /bar\b|pub\b|club|discoteca|nightlife|vida\s+noturna|live\s+music|casa\s+de\s+fados|lounge|cocktail|rooftop\s+bar/u,
  market: /mercado|market|feira|food\s?hall|farmers/u,
  sweets:
    /pastelaria|padaria|confeitaria|do[cç]aria|patisserie|bakery|boulangerie|panaderia|backerei|confiserie|pasticceria|tea\s?room|casa\s+de\s+ch[aá]|sweets?\s+shop|conventual|convent\s+sweets|traditional\s+sweets|regional\s+sweets|pastry\s+shop|gelateria|geladaria|sorveteria|ice\s?cream/u,
  breakfast:
    /breakfast|brunch|pequeno[\s-]*almo[cç]o|desayuno|colazione|fr[uü]hst[uü]ck|cafe|caf[eé]|coffee|pastelaria|padaria|bakery|boulangerie|patisserie|panaderia|tea\s?room|casa\s+de\s+ch[aá]|cafeteria/u,
};

/** Termos genéricos que não provam categoria nenhuma. */
const GENERIC =
  /^(ponto\s+de\s+interesse|point\s+of\s+interest|atra[cç][aã]o|attraction|local|place|spot|paragem|stop|visita|sightseeing|establishment|premise)s?$/u;

/**
 * Valida se um POI pertence realmente à categoria exclusiva pedida.
 * Sem categoria verificável (ou apenas um rótulo genérico) → excluído.
 */
export function stopMatchesFocus(
  focus: FocusCategory,
  stop: { name?: string | null; category?: string | null },
): boolean {
  const category = foldText(stop.category ?? "").trim();
  const name = foldText(stop.name ?? "").trim();
  if (!category && !name) return false;
  if (category && !GENERIC.test(category) && ALLOW[focus].test(category)) return true;
  if ((!category || GENERIC.test(category)) && name && ALLOW[focus].test(name)) return true;
  // Categoria verificável mas de outro tipo, ou apenas rótulo genérico.
  if (category && !GENERIC.test(category)) return ALLOW[focus].test(category) || ALLOW[focus].test(name);
  return false;
}

/** Aplica o filtro rígido a uma lista de paragens. */
export function filterStopsByFocus<T extends { name?: string | null; category?: string | null }>(
  focus: FocusCategory | null | undefined,
  stops: T[],
): T[] {
  if (!focus) return stops;
  return stops.filter((stop) => stopMatchesFocus(focus, stop));
}

/* ------------------------------------------------------------------ *
 * Deteção MULTI-FAMÍLIA de interesses turísticos explícitos (autoridade)
 *
 * `detectExclusiveCategory` acima mantém-se INALTERADA para todos os pedidos
 * e padrões já existentes, incluindo comida. Esta função é ADITIVA: devolve
 * TODAS as famílias turísticas reconhecidas (taxonomia global), em ordem
 * estável. Quando a lista não está vazia, é a autoridade do caminho
 * turístico — o `categoryFocus` legado mantém-se apenas por
 * compatibilidade/telemetria e NUNCA volta a filtrar a rota por uma única
 * família.
 * ------------------------------------------------------------------ */

import {
  detectExplicitTourismFamilies as detectFamiliesInTaxonomy,
  placeMatchesAnyTourismFamily,
  type TourismFamilyId,
} from "./tourism-taxonomy";

/**
 * Todas as famílias turísticas explicitamente pedidas (multilingue, ordem
 * estável). Comida não é família turística — os padrões de comida continuam
 * exclusivos do motor legado.
 */
export function detectExplicitTourismFamilies(text: string | null | undefined): TourismFamilyId[] {
  return detectFamiliesInTaxonomy(text);
}

/** Família turística → foco legado, só para rótulos/telemetria. */
export const TOURISM_FAMILY_FOCUS: Record<TourismFamilyId, FocusCategory> = {
  museums_culture: "museum",
  viewpoints_panoramas: "viewpoint",
  monuments_heritage: "monument",
  nature_parks: "nature",
  water_beaches: "water",
  neighbourhoods_markets: "market",
  stadiums_architecture: "monument",
  activities_attractions: "park",
  special_places: "park",
};

/**
 * Filtro MULTI-FAMÍLIA vinculativo: numa rota com famílias explícitas, toda
 * a paragem de visita pertence a PELO MENOS UMA família pedida. Usa tipos
 * do fornecedor + nome (relevância multi-sinal da taxonomia), nunca uma
 * etiqueta textual única.
 */
export function filterStopsByTourismFamilies<
  T extends { name?: string | null; category?: string | null; placeTypes?: string[] | null; placePrimaryType?: string | null },
>(families: TourismFamilyId[], stops: T[]): Array<{ stop: T; family: TourismFamilyId }> {
  const out: Array<{ stop: T; family: TourismFamilyId }> = [];
  for (const stop of stops) {
    const match = placeMatchesAnyTourismFamily(families, {
      name: stop.name,
      types: stop.placeTypes ?? null,
      primaryType: stop.placePrimaryType ?? null,
    });
    if (match.accepted && match.family) out.push({ stop, family: match.family });
  }
  return out;
}
